import { useModules, useTags } from '@entities/common';
import { useContacts, useLoadContacts } from '@entities/contacts';
import cn from 'classnames';
import { useEffect } from 'react';
import { FormProvider, type SubmitErrorHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { EditorPartitions } from '@widgets/programs/index.ts';
import { FormCostForGroupsEditor } from '@widgets/shared/Forms/CostForGroupsEditor/ui';
import {
  Actions,
  Block,
  Coach,
  Contacts,
  Cost,
  DateTime,
  Description,
  Format,
  Language,
  Place,
  Places,
  Prepayment,
  Refusal,
  Status,
  Title,
} from '@features/events/event-editor';
import { useEventEditor } from '@features/events/event-editor/model';
import { ArrowShortLeft } from '@shared/assets';
import { useMobile } from '@shared/hooks/index.ts';
// import { EditorLoadPromo } from '@widgets/programs/editor-load-promo';
import * as shared from '@shared/styles/sharedStyles.module.scss';
import {
  IconButton,
  ListBody,
  PageHead,
  PageTitle,
  TextButton,
  UploadProgressCard,
} from '@shared/ui';
import { FormVideo } from '@shared/ui/forms/video/index.ts';
import { useVisitors } from '@src/library/hooks/events';
import * as st from './styles.module.scss';

export const EditorPage = (): JSX.Element => {
  useLoadContacts();
  const { setSelectedContacts } = useContacts();
  const { id } = useParams<{ id: string }>();
  const { setSelectedTags, selectedTags } = useTags();
  const { setModule, moduleById } = useModules();
  const visitors = useVisitors(id);
  const hasTickets = visitors.status !== 'loading' && visitors.tickets.length > 0;
  const { isMobile } = useMobile();
  const {
    methods,
    onSubmit,
    event,
    editorStatus,
    mode,
    onClose,
    progress,
    hasDirtyFields,
    doSetStorageData,
    doRemoveStorageData,
    isLoading,
  } = useEventEditor();

  const isSubmitDisabled = !hasDirtyFields();
  const isEditMode = !isNaN(id as any);

  useEffect(() => {
    if (!selectedTags.length && event?.tags) {
      const module = moduleById(event.module);
      if (module) {
        setModule(module);
      }
      setSelectedTags(event.tags);
    }
  }, [isLoading]);

  const handleClickBack = () => {
    setSelectedContacts([]);
    doRemoveStorageData();
    onClose();
  };

  const error: SubmitErrorHandler<any> = (errors) => {
    console.log('Errors:', errors);
    console.log('FormState Errors:', methods.formState.errors);
  };

  return (
    <>
      <PageTitle title={id ? 'Редактирование мероприятия' : 'Новое мероприятие'} />
      <PageHead
        Left={
          <div className={st.button}>
            <IconButton variant="dark" onClick={handleClickBack}>
              <ArrowShortLeft />
            </IconButton>
          </div>
        }
        Right={
          <TextButton form="eventEdit" type="submit" disabled={isSubmitDisabled}>
            Готово
          </TextButton>
        }
      />

      <ListBody>
        {(!isLoading || !id) && (
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit, error)}
              className={cn(shared.forFormsGap20, st.editor)}
              id="eventEdit"
            >
              <Title />

              <Description />
              <EditorPartitions
                onRedirect={() => doSetStorageData(methods.getValues())}
                onModuleChange={(moduleId) =>
                  methods.setValue('module', moduleId, { shouldDirty: moduleId !== 0 })
                }
                onTagsChange={(tagIds) =>
                  methods.setValue('tags', tagIds, { shouldDirty: tagIds.length !== 0 })
                }
                from="events"
              />

              {/*<EditorModuleTags*/}
              {/*  getValues={methods.getValues}*/}
              {/*  reset={methods.reset}*/}
              {/*  setValue={methods.setValue}*/}
              {/*  onRedirect={() => doSetStorageData(methods.getValues())}*/}
              {/*  from={'events'}*/}
              {/*/>*/}

              <Language />

              <Format />

              <DateTime hasTickets={hasTickets} />

              <Place mode={mode} />

              <Places />

              <Cost />

              <FormCostForGroupsEditor
                onRedirect={() => doSetStorageData(methods.getValues())}
                nameOfArray={'costs'}
                formContextFromProps={methods}
                minCostValue={0}
                initData={event?.costs}
              />

              <Prepayment />

              <Refusal />

              <Coach />

              <FormVideo
                isEditMode={isEditMode}
                fieldLabel={isMobile ? 'Видео' : 'Презентационное видео'}
              />
              {/* <EditorLoadPromo
                onRedirect={() => doSetStorageData(methods.getValues())}
                promoId={methods.getValues('promo_video')}
                onPromoChange={(promoId) => methods.setValue('promo_video', promoId)}
                onRemove={removePromo}
              /> */}
              <Block />

              <Contacts
                onRedirect={() => doSetStorageData(methods.getValues())}
                contacts={event?.contacts.map((c) => c.id)}
              />

              <Actions hasTickets={hasTickets} />
            </form>
          </FormProvider>
        )}
        {editorStatus === 'loading' && <Status />}

        {editorStatus === 'videoLoading' && (
          <div className={st.uploadProgress}>
            <UploadProgressCard
              progress={progress}
              title="Загружаем видео, пожалуйста не закрывайте окно..."
            />
          </div>
        )}
      </ListBody>
    </>
  );
};
